/**
 * The adaptive allocation backtest API — Lab-native, same-origin, like `labStrategyApi`.
 *
 * The result types mirror the Core contracts (`AllocationDecision`, `SleeveAllocationDecision`)
 * as the server serialises them; fractions stay fractions here and the page formats them.
 */

const API_BASE = '/api';

export interface NavPoint { date: string; value: number }

export interface NavSummary {
  start_date: string | null;
  end_date: string | null;
  total_return: number | null;
  cagr: number | null;
  annualized_volatility: number | null;
  sharpe: number | null;
  sortino: number | null;
  max_drawdown: number | null;
  upi: number | null;
}

export interface Arm {
  name: string;
  type?: 'fixed' | 'regime';
  weights?: Record<string, number>;
  weights_by_regime?: Record<string, Record<string, number>>;
  summary: NavSummary;
  gross_turnover: number;
  rotation_turnover: number;
  total_rotation_cost: number;
  nav: NavPoint[];
}

export interface SleeveMetric {
  metric: string;
  lookback_months: number;
  signal_weight: number;
  value: number | string | null; // ±Infinity arrives as a string
  window_start: string | null;
  window_end: string | null;
  observations: number;
  rank_score: number | null;
}

export interface SleeveDecision {
  sleeve_key: string;
  enabled: boolean;
  metrics: SleeveMetric[];
  composite_score: number | null;
  performance_rank: number | null;
  performance_weight: number | null;
  annualized_volatility: number | null;
  erc_weight: number | null;
  raw_weight: number | null;
  min_weight: number;
  max_weight: number;
  constrained_weight: number | null;
  previous_weight: number | null;
  smoothed_weight: number | null;
  max_step_adjustment: number | null;
  final_weight: number | null;
}

export interface Issue { code: string; message: string; sleeve_key: string | null }

export interface Decision {
  config_hash: string;
  allocator_version: string;
  signal_date: string;
  execution_date: string;
  regime: string | null;
  status: 'rebalanced' | 'held' | 'failed';
  hold_reason: string | null;
  weights: Record<string, number>;
  previous_weights: Record<string, number> | null;
  sleeves: SleeveDecision[];
  turnover: number;
  strategy_rotation_cost_bps: number;
  estimated_rotation_cost: number;
  warnings: Issue[];
  errors: Issue[];
}

export interface AdaptiveResult {
  portfolio_name: string;
  start_date: string;
  end_date: string;
  sleeve_history_start: string;
  first_execution_date: string;
  config: Record<string, unknown>;
  config_hash: string;
  allocator_version: string;
  sleeve_costs_bps: number;
  adaptive: Arm & { rotation_cost_bps: number; rebalance_count: number; held_count: number };
  benchmarks: Arm[];
  sleeves: { key: string; name: string; cash_days: number; summary: NavSummary; nav: NavPoint[] }[];
  allocation_history: { date: string; sleeve: string; target_weight: number }[];
  rebalances: Decision[];
  latest_decision: Decision;
  failures_before_start: number;
  held_after_failure: Decision[];
  warnings: string[];
}

export interface AdaptiveRequest {
  strategy_id: number;
  start_date: string;
  end_date: string;
  allocation?: Record<string, unknown> | null;
  benchmarks?: Record<string, unknown>[] | null;
  sleeve_costs_bps?: number;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    let detail = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      if (typeof body?.detail === 'string') detail = body.detail;
    } catch {
      // keep the status text
    }
    throw new Error(detail);
  }
  return response.json() as Promise<T>;
}

export const adaptiveApi = {
  submit: (body: AdaptiveRequest) =>
    call<{ run_id: number; status: string }>('/backtest/adaptive/submit', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  result: (runId: number) =>
    call<{ run_id: number; status: 'running' | 'complete' | 'error'; result: AdaptiveResult | null; error: string | null }>(
      `/backtest/adaptive/result/${runId}`,
    ),
};
